import { useRef } from "react";
import { useForm, useWatch } from "react-hook-form";

import {
  type ConstraintListItemDto,
  type PartnerCategoriesDto,
  type ProjectConnectionDetailsDto,
  type ProjectConnectionDto,
  type UserRoleServicePartnerPagePermissionsDto,
} from "@/api/types";
import bgPlaceholder from "@/assets/images/image-placeholder.png";
import { Accordion } from "@/components/Accordion/Accordion";
import { AudienceSelector } from "@/components/AudienceSelector/AudienceSelector";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormContent } from "@/components/Form/FormContent";
import { FormField } from "@/components/Form/FormField";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadio, FormRadioGroup } from "@/components/Form/FormRadio";
import { FormRichTextEditor } from "@/components/Form/FormRichTextEditor";
import { FormSelect } from "@/components/Form/FormSelect";
import { InfoCircleIcon } from "@/components/Icons/InfoCircleIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { PreviewLayout } from "@/components/Layouts/PreviewLayout";
import { Notice } from "@/components/Notice/Notice";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { PermissionListLayout, PermissionWrapper } from "@/components/Permission/Permission";
import { AppServicePartnerDetailsView } from "@/components/Phone/AppServicePartnerDetailsView";
import { Phone } from "@/components/Phone/Phone";
import type { RichTextEditorRef } from "@/components/RichTextEditor/RichTextEditor";
import { Wizard, WizardStep } from "@/components/Wizard/Wizard";
import { getFlagIcon } from "@/helpers/flags";
import type { FormPermissions } from "@/helpers/permissions";
import {
  createEmailValidRule,
  createImageSizeRule,
  createImageTypeRule,
  createRequiredStringRule,
} from "@/helpers/rules";
import { isValidPhoneNumber } from "@/helpers/validation";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

type ServicePartnerType = "general" | "iqbi" | "myPup";

export interface LayoutProps {
  isEditMode: boolean;
  allowedTypes: ServicePartnerType[];
  categories: PartnerCategoriesDto[];
  defaultValues: CreateOrEditServicePartnerFormValues;
  isSubmitting: boolean;
  connections: ProjectConnectionDetailsDto[];
  onSubmit: (data: CreateOrEditServicePartnerFormValues) => void;
}

const MAX_LENGTH = 128;

export function Layout({
  defaultValues,
  isSubmitting,
  connections,
  allowedTypes,
  isEditMode,
  onSubmit,
  categories,
}: LayoutProps): React.ReactNode {
  const editorRef = useRef<RichTextEditorRef>(null);
  const { t } = useTranslation();
  const user = useSessionUser();
  const slug = useSlug();
  const sessionUser = useSessionUser();
  const formMethods = useForm<CreateOrEditServicePartnerFormValues>({
    mode: "onChange",
    defaultValues: defaultValues,
  });

  const connection = useWatch<CreateOrEditServicePartnerFormValues, "connection">({
    control: formMethods.control,
    name: "connection",
  });
  const audience = useWatch<CreateOrEditServicePartnerFormValues, "audience">({
    control: formMethods.control,
    name: "audience",
  });
  const canLeave = () => formMethods.trigger();

  const showIntegrationOptions = isEditMode
    ? defaultValues.type !== "general"
    : allowedTypes.length > 1 || !allowedTypes.includes("general");
  const title = isEditMode
    ? t("page.service.create-or-edit.edit-mode.title")
    : t("page.service.create-or-edit.create-mode.title");

  return (
    <DocumentPaper
      theme="wide"
      title={title}
      subTitle={
        <Breadcrumbs
          pages={[
            {
              name: t("page.service.list.title"),
              to: routes.servicePartners.list({
                slug,
              }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form formMethods={formMethods} onSubmit={onSubmit}>
        <Wizard
          id="service-create-wizard"
          actionsText={{
            finish: isEditMode ? t("component.wizard.action.edit.finish") : t("component.wizard.action.create.finish"),
          }}
          isSubmitting={isSubmitting}
          onFinish={formMethods.handleSubmit(onSubmit)}
          strictOrder={!isEditMode}
        >
          <WizardStep
            id={1}
            canLeave={canLeave}
            hasFinish={isEditMode}
            title={
              <>
                <span>
                  {t("component.wizard.step", {
                    step: 1,
                  })}
                </span>
                <span>{t("component.wizard.step-general")}</span>
              </>
            }
          >
            <PreviewLayout
              preview={<Preview />}
              showPreviewButtonText={t("page.service.create-or-edit.app-preview.open")}
              previewTitleText={t("page.service.create-or-edit.app-preview.title")}
              previewWarning={t("page.service.create-or-edit.app-preview.warning")}
            >
              <FormContent maxWidth="xl">
                <Notice
                  icon={<InfoCircleIcon />}
                  styling="filled"
                  type="info"
                  message={t("common.label.your-input-will-be-auto-translated")}
                />
                {showIntegrationOptions && (
                  <FormField label={t("model.service.type")}>
                    <FormRadioGroup>
                      {(defaultValues.type === "general" || allowedTypes.includes("general")) && (
                        <FormRadio<CreateOrEditServicePartnerFormValues>
                          name="type"
                          value="general"
                          label={t("model.service.type.general")}
                          rules={{
                            required: {
                              message: t("components.form.error.required", {
                                inputName: t("model.service.type"),
                              }),
                              value: true,
                            },
                          }}
                          disabled={isEditMode}
                        />
                      )}
                      {(defaultValues.type === "iqbi" || allowedTypes.includes("iqbi")) && (
                        <FormRadio<CreateOrEditServicePartnerFormValues>
                          name="type"
                          value="iqbi"
                          label={t("model.service.type.iqbi")}
                          rules={{
                            required: {
                              message: t("components.form.error.required", {
                                inputName: t("model.service.type"),
                              }),
                              value: true,
                            },
                          }}
                          disabled={isEditMode}
                        />
                      )}
                      {(defaultValues.type === "myPup" || allowedTypes.includes("myPup")) && (
                        <FormRadio<CreateOrEditServicePartnerFormValues>
                          name="type"
                          value="myPup"
                          label={t("model.service.type.mypup")}
                          rules={{
                            required: {
                              message: t("components.form.error.required", {
                                inputName: t("model.service.type"),
                              }),
                              value: true,
                            },
                          }}
                          disabled={isEditMode}
                        />
                      )}
                    </FormRadioGroup>
                  </FormField>
                )}
                <FormField
                  htmlFor="title-input"
                  label={t("model.service.title")}
                  required
                  actions={getFlagIcon({
                    languageId: sessionUser.language.id,
                    width: 16,
                  })}
                >
                  <FormInput<CreateOrEditServicePartnerFormValues>
                    className="max-w-xl"
                    id="title-input"
                    name="title"
                    placeholder={t("model.service.title.placeholder")}
                    data-testid="service-name"
                    rules={{
                      maxLength: {
                        message: t("components.form.error.max-length", {
                          length: MAX_LENGTH,
                        }),
                        value: MAX_LENGTH,
                      },
                      validate: {
                        required: createRequiredStringRule(t, "model.service.title"),
                      },
                    }}
                  />
                </FormField>
                <FormField
                  htmlFor="subtitle-input"
                  label={t("model.service.subtitle")}
                  description={t("model.service.subtitle.description")}
                  actions={getFlagIcon({
                    languageId: sessionUser.language.id,
                    width: 16,
                  })}
                  required
                >
                  <FormInput<CreateOrEditServicePartnerFormValues>
                    className="max-w-xl"
                    id="subtitle-input"
                    name="subtitle"
                    placeholder={t("model.service.subtitle.placeholder")}
                    data-testid="service-slogan"
                    rules={{
                      maxLength: {
                        message: t("components.form.error.max-length", {
                          length: MAX_LENGTH,
                        }),
                        value: MAX_LENGTH,
                      },
                      validate: {
                        required: createRequiredStringRule(t, "model.service.subtitle"),
                      },
                    }}
                  />
                </FormField>
                <FormField
                  htmlFor="description-input"
                  label={t("model.service.description")}
                  actions={getFlagIcon({
                    languageId: sessionUser.language.id,
                    width: 16,
                  })}
                  required
                >
                  <FormRichTextEditor<CreateOrEditServicePartnerFormValues>
                    editorRef={(ref) => {
                      editorRef.current = ref;
                    }}
                    name="description"
                    placeholder={t("model.service.description.placeholder")}
                    data-testid="description-input"
                    rules={{
                      required: {
                        message: t("components.form.error.required", {
                          inputName: t("model.service.description"),
                        }),
                        value: true,
                      },
                    }}
                  />
                </FormField>
                {defaultValues.type === "iqbi" && (
                  <FormField htmlFor="type" label={t("model.service.type")} required>
                    <FormRadioGroup>
                      <FormRadio<CreateOrEditServicePartnerFormValues, "type">
                        name="type"
                        value="general"
                        label={t("model.service.type.general")}
                        disabled
                      />
                      <FormRadio<CreateOrEditServicePartnerFormValues, "type">
                        name="type"
                        value="iqbi"
                        label={t("model.service.type.iqbi")}
                        disabled
                      />
                    </FormRadioGroup>
                  </FormField>
                )}
                <FormField
                  htmlFor="category"
                  label={t("model.service.category")}
                  description={t("model.service.category.description")}
                  required
                >
                  <FormSelect<CreateOrEditServicePartnerFormValues, PartnerCategoriesDto>
                    id="category"
                    name="category"
                    placeholder={t("model.service.category.placeholder")}
                    items={categories}
                    keySelector={({ id }) => id}
                    renderOption={(tab) => tab.name}
                    data-testid="service-category"
                    rules={{
                      required: {
                        message: t("components.form.error.required", {
                          inputName: t("model.service.category"),
                        }),
                        value: true,
                      },
                    }}
                  />
                </FormField>
                <FormField
                  htmlFor="email"
                  label={t("model.service.email")}
                  description={t("model.service.email.description")}
                >
                  <FormInput<CreateOrEditServicePartnerFormValues, "email">
                    id="email"
                    name="email"
                    type="email"
                    inputMode="email"
                    data-testid="service-email"
                    rules={{
                      validate: {
                        isValid: createEmailValidRule(t),
                      },
                    }}
                  />
                </FormField>
                <FormField
                  htmlFor="phoneNumber"
                  label={t("model.service.phone-number")}
                  description={t("model.service.phone-number.description")}
                >
                  <FormInput<CreateOrEditServicePartnerFormValues, "phoneNumber">
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    inputMode="tel"
                    data-testid="service-phone"
                    rules={{
                      validate: {
                        isValid(value) {
                          value = value?.trim();
                          if (!value) {
                            return;
                          }

                          return isValidPhoneNumber(value)
                            ? undefined
                            : t("components.form.error.invalid-phone-number");
                        },
                      },
                    }}
                  />
                </FormField>
                <FormField
                  htmlFor="profileImage"
                  label={t("model.service.profile-image")}
                  description={t("model.service.profile-image.description")}
                  required
                >
                  <FormImageInput<CreateOrEditServicePartnerFormValues, "profileImage">
                    name="profileImage"
                    id="profileImage"
                    cropper
                    data-testid="service-profile-image"
                    rules={{
                      required: t("components.form.error.required", {
                        inputName: t("model.service.profile-image"),
                      }),
                      validate: {
                        size: createImageSizeRule({
                          t,
                        }),
                        type: createImageTypeRule({
                          t,
                        }),
                      },
                    }}
                  />
                </FormField>
                <FormField
                  htmlFor="headerImage"
                  label={t("model.service.header-image")}
                  description={t("model.service.header-image.description")}
                >
                  <FormImageInput<CreateOrEditServicePartnerFormValues, "headerImage">
                    name="headerImage"
                    id="headerImage"
                    data-testid="service-header-image"
                    rules={{
                      validate: {
                        size: createImageSizeRule({
                          t,
                        }),
                        type: createImageTypeRule({
                          t,
                        }),
                      },
                    }}
                  />
                </FormField>
                {user.isAdmin && (
                  <div className="flex w-full flex-col gap-2">
                    <FormField
                      label={t("common.entity.audience")}
                      description={t("component.audience-section.description", {
                        audienceTarget: t("common.entity.service").toLowerCase(),
                      })}
                    >
                      {connections.length > 0 && (
                        <FormField label={t("page.service.create-or-edit.connection.label")} htmlFor="connection">
                          <FormSelect<CreateOrEditServicePartnerFormValues, ProjectConnectionDto>
                            id="connection"
                            name="connection"
                            items={connections}
                            keySelector={(x) => x.id}
                            renderOption={(x) => x.name}
                            emptyItem={t("page.service.create-or-edit.connection.none")}
                          />
                        </FormField>
                      )}
                    </FormField>
                    {!connection && (
                      <AudienceSelector
                        defaultAudience={audience}
                        isEditMode={isEditMode}
                        onSaveAudience={(newAudience) => formMethods.setValue("audience", newAudience)}
                      />
                    )}
                  </div>
                )}
              </FormContent>
            </PreviewLayout>
          </WizardStep>
          <WizardStep
            id={2}
            hasFinish={isEditMode}
            title={
              <>
                <span>
                  {t("component.wizard.step", {
                    step: 2,
                  })}
                </span>
                <span>{t("component.wizard.step-permissions")}</span>
              </>
            }
            isHidden={!user.isSuperAdmin}
            {...{
              canLeave,
            }}
          >
            <FormContent maxWidth="none">
              {/* While we only support super admins you shouldn't be able to set permissions for connections. When supporting admins we should remove this */}
              {connection ? (
                <p className="py-6 pl-2 text-grey-700">
                  {t("page.service.create-or-edit.permissions.disabled-for-connection")}
                </p>
              ) : null}
              <div className={connection ? "hidden" : undefined}>
                <Accordion.Root>
                  {Object.entries(defaultValues.permissions).map(([roleId, permission]) => (
                    <Accordion.Item key={permission.roleId} value={permission.roleId}>
                      <Accordion.Trigger>{permission.name}</Accordion.Trigger>
                      <Accordion.Content>
                        <PermissionListLayout>
                          <PermissionWrapper>
                            <FormCheckbox<CreateOrEditServicePartnerFormValues>
                              alignTop
                              disabled={!!connection}
                              memorise={defaultValues.permissions[roleId]?.canManageContent || false}
                              name={`permissions.${roleId}.canManageContent`}
                              label={t("model.permission.service-page.can-manage-content")}
                            />
                          </PermissionWrapper>
                        </PermissionListLayout>
                      </Accordion.Content>
                    </Accordion.Item>
                  ))}
                </Accordion.Root>
              </div>
            </FormContent>
          </WizardStep>
        </Wizard>
      </Form>
    </DocumentPaper>
  );
}
export interface CreateOrEditServicePartnerFormValues {
  title: string;
  subtitle: string;
  description: string;
  audience: ConstraintListItemDto[];
  connection?: ProjectConnectionDto;
  permissions: FormPermissions<UserRoleServicePartnerPagePermissionsDto>;
  category?: PartnerCategoriesDto;
  email?: string;
  phoneNumber?: string;
  profileImage?: FormImage[];
  headerImage?: FormImage[];
  type: ServicePartnerType;
}
function Preview() {
  const user = useSessionUser();
  const { email, phoneNumber, title, subtitle, description, profileImage, headerImage } =
    useWatch<CreateOrEditServicePartnerFormValues>();

  return (
    <Phone>
      <AppServicePartnerDetailsView
        name={title ?? ""}
        slogan={subtitle}
        description={description ?? ""}
        profileImage={profileImage?.[0]?.url}
        headerImage={headerImage?.[0]?.url ?? user.project.backgroundImage?.url ?? bgPlaceholder}
        email={email}
        phoneNumber={phoneNumber}
        projectTintColor={user.project?.styling.tintColor}
      />
    </Phone>
  );
}
